import { Slot, Slottable } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { SolidCheck, SolidSpinner } from '@/icons/components';
import { IconComponent } from '@/icons/iconTypes';
import { twJoin } from '@/utils/tw-merge';

const buttonVariants = cva(
  [
    'inline-flex items-center whitespace-nowrap ring-inset focus-visible:outline-none',
    'aria-disabled:cursor-not-allowed aria-disabled:bg-gray-300 aria-disabled:text-gray-600 aria-disabled:ring-0',
    'disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 disabled:ring-0',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: [
          'bg-blue-500 text-white',
          'hover:bg-blue-800',
          'active:bg-blue-700 [&.active]:bg-blue-700',
          'focus-visible:bg-white focus-visible:text-blue-500 focus-visible:ring focus-visible:ring-blue-500',
        ].join(' '),
        secondary: [
          'bg-blue-100 text-blue-800',
          'hover:bg-blue-50 hover:ring-1 hover:ring-blue-200',
          'active:bg-blue-100 active:ring-1 active:ring-blue-500 [&.active]:bg-blue-100 [&.active]:ring-blue-500',
          'focus-visible:bg-white focus-visible:text-blue-500 focus-visible:ring focus-visible:ring-blue-500',
        ].join(' '),
        tertiary: [
          'bg-gray-200 text-black',
          'hover:bg-gray-300',
          'active:bg-gray-400 [&.active]:bg-gray-400',
          'focus-visible:bg-blue-500 focus-visible:bg-white focus-visible:ring focus-visible:ring-blue-500',
        ].join(' '),
        white: [
          'text-black',
          'hover:bg-gray-200',
          'active:bg-gray-300 [&.active]:text-blue-500',
          'focus-visible:bg-blue-500 focus-visible:bg-white focus-visible:ring focus-visible:ring-blue-500',
        ].join(' '),
        filter: [
          'bg-white text-black ring-1 ring-blue-500',
          'hover:bg-blue-500 hover:text-white',
          'active:bg-blue-800 active:text-white [&.active]:bg-blue-800 [&.active]:text-white',
          'focus-visible:ring',
        ].join(' '),
      },
      size: {
        default: 'style-button-label h-5 justify-start gap-1 rounded-sm px-2',
        icon: 'style-button-label size-5 justify-center rounded-sm',
        iconSmall: 'size-3 justify-center rounded-xs',
        small: 'style-extra-small-label h-3 gap-0.5 rounded-xs px-1',
        xl: 'style-body h-11 flex-col justify-center gap-0.5 rounded-sm p-2',
        fullWidth:
          'style-button-label h-5 w-full justify-start gap-1 rounded-sm px-2',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  state?: 'loading' | 'success' | false;
  icon?: IconComponent;
  iconClassname?: string;
  iconPosition?: 'left' | 'right';
  active?: boolean;
  'data-testid'?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      variant,
      size,
      asChild = false,
      icon,
      iconClassname,
      active,
      state,
      disabled,
      iconPosition = 'left',
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : 'button';

    let IconComponent = icon;

    if (state === 'loading') {
      IconComponent = SolidSpinner;
    } else if (state === 'success') {
      IconComponent = SolidCheck;
    }

    let buttonSize = 16;

    if (size === 'small') buttonSize = 14;
    if (size === 'iconSmall') buttonSize = 14;
    if (size === 'xl') buttonSize = 18;

    return (
      <Component
        className={twJoin(
          buttonVariants({ variant, size, className }),
          active && 'active',
          // Prevent multiple form submissions when the button is in a success state
          state === 'success' && 'pointer-events-none'
        )}
        ref={ref}
        disabled={disabled || state === 'loading'}
        {...props}
      >
        {IconComponent && iconPosition === 'left' && (
          <IconComponent
            size={buttonSize}
            className={twJoin(
              state === 'loading' && 'animate-spin',
              iconClassname
            )}
          />
        )}

        <Slottable>{children}</Slottable>

        {IconComponent && iconPosition === 'right' && (
          <IconComponent
            size={buttonSize}
            className={twJoin(
              state === 'loading' && 'animate-spin',
              iconClassname
            )}
          />
        )}
      </Component>
    );
  }
);
Button.displayName = 'Button';
